import { useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { useAccount } from '@/frontastic';
import { getAgentName, getCookieValue, TranslateText } from '@/helpers/utils/siteUtils';
import { PhoneRecordingPayload } from '@/sdk/composable-commerce/types/payloads/AccountPayloads';
import { COOKIE_KEYS, DEFAULT_SITE_LANG } from '../../../../../../constants/constant';
import AdjustPaymentMethodModal from '../../../modals/orderDetails/adjustPaymentMethod';
import { Button } from '../../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/card';
import { maskPhoneNumber, maskPostalCode } from '../../../utils/formatUtils';
import { PageTexts } from '../../i18n';
import { Payment, ShippingAndBillingDetailsType, billingAddress } from '../../types';
import { getPaymentMethodSvgs } from '../../util/creditCardIcon';

const CustomerCreditNotes = ({
  billingDetails,
  customerId,
  isShippingMethodAdjustable,
  billingAddress,
  paymentMethods,
  getUpdatedData,
  orderBalance,
  addNewNotes,
  batchId,
  showActiveBrandSection = true,
}: {
  billingDetails: ShippingAndBillingDetailsType;
  isShippingMethodAdjustable: boolean;
  customerId?: string;
  billingAddress: billingAddress;
  paymentMethods: Payment;
  getUpdatedData: () => void;
  orderBalance: number | undefined;
  addNewNotes: (note: string) => void;
  batchId?: string;
  showActiveBrandSection?: boolean;
}) => {
  const Texts = TranslateText(PageTexts, DEFAULT_SITE_LANG);
  const [open, setOpen] = useState(false);
  const { startPhoneRecording, stopPhoneRecording } = useAccount();
  const params = useSearchParams();

  const startRecording = async () => {
    const payload = {
      data: {
        agentEmailAddress: getAgentName(),
        divisionCode: getCookieValue(COOKIE_KEYS?.selectedSite)?.slice(0, 2) ?? '',
      },
    } as PhoneRecordingPayload;
    await startPhoneRecording(payload);
  };

  const stopRecording = async () => {
    const payload = {
      data: {
        agentEmailAddress: getAgentName(),
        divisionCode: getCookieValue(COOKIE_KEYS?.selectedSite)?.slice(0, 2) ?? '',
      },
    } as PhoneRecordingPayload;
    await stopPhoneRecording(payload);
  };

  return (
    <Card className="mb-5 w-full rounded-md border-foggyGray shadow-lg md:mb-0">
      <CardHeader className="p-5">
        <CardTitle className="flex flex-wrap">
          <p className="mr-auto text-lg font-bold">{Texts.Payment}</p>
          {isShippingMethodAdjustable && showActiveBrandSection ? (
            <Button
              onClick={() => {
                setOpen(true);
                stopRecording();
              }}
              className="rounded-md border-[1px] border-foggyGray px-3 py-1.5 text-xs font-medium"
            >
              {Texts.adjustPaymentMethod}
            </Button>
          ) : (
            <></>
          )}
        </CardTitle>

        <AdjustPaymentMethodModal
          customerId={customerId}
          Texts={Texts}
          open={open}
          setOpen={setOpen}
          billingAddress={billingAddress}
          getUpdatedData={getUpdatedData}
          startRecording={startRecording}
          orderBalance={orderBalance}
          addNewNotes={addNewNotes}
        ></AdjustPaymentMethodModal>
      </CardHeader>
      <CardContent>
        <ul>
          {getPaymentMethodSvgs(
            paymentMethods?.PaymentInfo?.paymentType,
            [paymentMethods],
            Texts,
            billingDetails,
            params,
            batchId,
          )}
          <li className="mb-8 flex">
            <p className="w-1/3">{Texts.billingAddress}:</p>
            <p className="gbmask w-2/3">
              {billingAddress?.firstName + ' ' + billingAddress?.lastName}
              <br />
              {billingAddress?.streetName}
              <br />
              {billingAddress?.additionalAddressInfo} {billingAddress?.city}, {billingAddress?.state}{' '}
              {maskPostalCode(billingAddress?.postalCode ?? '')}
              <br />
              {maskPhoneNumber(billingAddress?.phone ?? '')}
            </p>
          </li>
          <li className="mb-8 flex">
            <p className="w-1/3">{Texts.contactInfo}:</p>
            <p className="gbmask w-2/3 break-words">{billingDetails?.orderInfo?.emailAddress}</p>
          </li>
        </ul>
      </CardContent>
    </Card>
  );
};

export default CustomerCreditNotes;
